import {
  buildConditionAnalysisSlices,
  cohortHeadlineFromNode,
  type ConditionAnalysisBucketRow,
  type ConditionAnalysisNode,
  type ConditionAnalysisSituationRow,
  type ConditionAnalysisSummaryRow,
} from "./conditionAnalysisSelectors";
import type { ReportPick } from "./reportPickListStorage";

export type PickQuickStats = {
  tradeCount?: number;
  totalPnl?: number;
  /** Percent 0–100 when present. */
  winRate?: number;
  expectancy?: number;
};

type PickQuickStatsMeta = {
  trade_count?: number;
  total_pnl?: number;
  win_rate?: number;
  expectancy?: number;
};

export function pickQuickStatsFromMetrics(row: {
  sample_size?: number;
  total_pnl?: number;
  win_rate?: number;
  expectancy?: number;
}): PickQuickStats {
  return {
    tradeCount: row.sample_size,
    totalPnl: row.total_pnl,
    winRate: row.win_rate,
    expectancy: row.expectancy,
  };
}

/** Merge into pick `meta` when saving a pick from a table row or slice headline. */
export function quickStatsMetaPatch(stats: PickQuickStats): Record<string, unknown> {
  const q: PickQuickStatsMeta = {};
  if (stats.tradeCount != null && Number.isFinite(stats.tradeCount)) q.trade_count = stats.tradeCount;
  if (stats.totalPnl != null && Number.isFinite(stats.totalPnl)) q.total_pnl = stats.totalPnl;
  if (stats.winRate != null && Number.isFinite(stats.winRate)) q.win_rate = stats.winRate;
  if (stats.expectancy != null && Number.isFinite(stats.expectancy)) q.expectancy = stats.expectancy;
  return Object.keys(q).length ? { quick_stats: q } : {};
}

function readQuickStatsFromMeta(meta: Record<string, unknown> | undefined): PickQuickStats | null {
  if (!meta || typeof meta !== "object") return null;
  const raw = meta.quick_stats;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const q = raw as PickQuickStatsMeta;
  return {
    tradeCount: q.trade_count,
    totalPnl: q.total_pnl,
    winRate: q.win_rate,
    expectancy: q.expectancy,
  };
}

function hasAnyPickQuickStat(stats: PickQuickStats | null): stats is PickQuickStats {
  if (!stats) return false;
  return (
    (stats.tradeCount != null && Number.isFinite(stats.tradeCount)) ||
    (stats.totalPnl != null && Number.isFinite(stats.totalPnl)) ||
    (stats.winRate != null && Number.isFinite(stats.winRate)) ||
    (stats.expectancy != null && Number.isFinite(stats.expectancy))
  );
}

function findSummaryRow(
  node: ConditionAnalysisNode,
  tableKey: string,
  rowKey: string
): ConditionAnalysisSummaryRow | undefined {
  const rs = node.regime_summary;
  if (!rs) return undefined;
  const table = (rs as Record<string, ConditionAnalysisSummaryRow[] | undefined>)[tableKey];
  if (!Array.isArray(table)) return undefined;
  return table.find((r) => r.key === rowKey);
}

function resolveFromSnapshot(pick: ReportPick, snapshot: unknown): PickQuickStats | null {
  const slices = buildConditionAnalysisSlices(snapshot);
  if (!slices.length) return null;

  const sliceId = String(pick.sliceId ?? pick.meta?.slice_id ?? pick.sliceContext?.sliceId ?? "all").trim() || "all";
  const slice = slices.find((s) => s.id === sliceId) ?? slices.find((s) => s.kind === "all");
  if (!slice) return null;

  const node = slice.node;

  if (pick.kind === "bucket") {
    const name = String(pick.meta?.bucket ?? pick.label ?? "");
    for (const list of [node.best_conditions, node.worst_conditions] as Array<
      ConditionAnalysisBucketRow[] | undefined
    >) {
      const row = list?.find((r) => r.bucket === name);
      if (row) return pickQuickStatsFromMetrics(row);
    }
  }

  if (pick.kind === "situation") {
    const sit = String(pick.meta?.situation ?? pick.label ?? "");
    for (const list of [
      node.market_condition_brackets?.best_situations,
      node.market_condition_brackets?.worst_situations,
    ] as Array<ConditionAnalysisSituationRow[] | undefined>) {
      const row = list?.find((r) => (r.situation ?? "") === sit);
      if (row) return pickQuickStatsFromMetrics(row);
    }
  }

  const summaryTable = pick.meta?.summary_table;
  const summaryRowKey = pick.meta?.row_key;
  if (typeof summaryTable === "string" && typeof summaryRowKey === "string") {
    const row = findSummaryRow(node, summaryTable, summaryRowKey);
    if (row) return pickQuickStatsFromMetrics(row);
  }

  const ctxSliceId = pick.sliceContext?.sliceId?.trim();
  if (ctxSliceId) {
    const ctxSlice = slices.find((s) => s.id === ctxSliceId);
    if (ctxSlice) {
      const c = cohortHeadlineFromNode(ctxSlice.node);
      const stats: PickQuickStats = {
        tradeCount: c.sample,
        totalPnl: c.totalPnl,
        winRate: c.winRate,
        expectancy: c.expectancy,
      };
      if (hasAnyPickQuickStat(stats)) return stats;
    }
  }

  const cohort = cohortHeadlineFromNode(node);
  const fallback: PickQuickStats = {
    tradeCount: cohort.sample,
    totalPnl: cohort.totalPnl,
    winRate: cohort.winRate,
    expectancy: cohort.expectancy,
  };
  return hasAnyPickQuickStat(fallback) ? fallback : null;
}

export function resolvePickQuickStats(
  pick: ReportPick,
  snapshot?: unknown | null
): PickQuickStats | null {
  const fromMeta = readQuickStatsFromMeta(pick.meta);
  if (hasAnyPickQuickStat(fromMeta)) return fromMeta;
  if (snapshot == null) return null;
  return resolveFromSnapshot(pick, snapshot);
}

export function formatPickQuickStatsPnl(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}`;
}

export function formatPickQuickStatsWinRate(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)}%`;
}

export function formatPickQuickStatsExpectancy(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(1);
  if (abs >= 10) return value.toFixed(2);
  return value.toFixed(3);
}

export function formatPickQuickStatsTradeCount(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return String(Math.round(value));
}
